import * as React from 'react';
import { getLocalization } from 'global/global';
import { Alert, Form } from 'react-bootstrap';
import Select from 'react-dropdown-select';
import { toast } from 'react-toastify';
import { User, Users } from 'Interfaces/User';
import { formatDate, validEmail } from 'utils/utils';
import { saveSetting } from 'actions/userActions';
import { useAppDispatch, useAppSelector } from 'index';
import { getSharerEmail, sendActionEmail } from 'actions/shareActions';
import { ConfirmationModal } from '../ConfirmationModal';

export interface ShareActionsModalProps {
  link: string;
  questionId: string;
  tableFormId: string;
  id: string;
  auto: boolean;
  onClose: () => void;
}

let abortController = new AbortController();

const wabtecMessage = `Hi

  I am sharing the results of the audit carried out by Wabtec.
  Please add Actions, Owners and Plan Dates to the PDCA table.

  Best Regards`;

export default function ShareActionsModal(props: ShareActionsModalProps) {

  const dispatch = useAppDispatch();
  const clientPersist = useAppSelector(state => state.clientPersist);
  const users = useAppSelector(state => state.users);

  const { instance, accessSharedDataOnly } = clientPersist;

  const [value, setValue] = React.useState<Users>([]);
  const [unregisteredUsers] = React.useState(''); // setUnregisteredUsers
  const [accessUntil, setAccessUntil] = React.useState('');
  const [accessUntilVisible, setAccessUntilVisisble] = React.useState(true);
  const [groupError, setGroupError] = React.useState<string[]>([]);
  // const [hasError, setHasError] = React.useState(false);
  const [emailBody, setEmailBody] = React.useState(instance === 'wabtec' ? wabtecMessage : '');
  const [fixedEmail, setFixedEmail] = React.useState('');
  const [width, setWidth] = React.useState('');

  const [newUsers, setNewUsers] = React.useState<string[]>([]);

  const closeModal = () => {
    setValue([]);
    setWidth('');
    props.onClose();
  };

  React.useEffect(() => {
    if (props.id && accessSharedDataOnly === true) {
      if (abortController === null) {
        abortController = new AbortController();
      }
      dispatch(getSharerEmail(props.id, abortController.signal)).then(
        response => response.json()
      ).then(json => {
        if (json.email && validEmail(json.email)) {
          setFixedEmail(json.email);
          setValue(users.filter(u => u.name === json.userName));
          setAccessUntilVisisble(false);
        } else if (json.userName && validEmail(json.userName)) {
          setFixedEmail(json.userName);
          setValue(users.filter(u => u.name === json.userName));
          setAccessUntilVisisble(false);
        }
      }).catch(err => {
        console.error(err);
      });
    }
  }, [props.id]);

  const handleOnChange = (value) => {
    setValue(value);
  };

  const allUsers = React.useMemo(() => {
    return users.filter(u => !u.name.endsWith('-deleted'));
  }, [users]);

  const onCreateNew = (u: User) => {
    if (validEmail(u.name)) {
      setNewUsers(newUsers.concat([u.name]));
    }
  };

  const modalBody = (
    <div>
      {groupError.length > 0 && (
        <Alert variant={'warning'}>
          {getLocalization('differentGroup').replace('{{users}}', groupError.join(', '))}
        </Alert>
      )}
      <Form.Group className="mb-2">
        <Form.Label>{getLocalization('shareWith')}</Form.Label>
        <div className={'w-100 position-relative'}>
          {fixedEmail !== '' ? (
            <span>{fixedEmail}</span>
          ) : width !== '' && (
            <Select
              labelField={'name'}
              valueField={'id'}
              options={allUsers}
              onChange={(v) => handleOnChange(v)}
              values={value}
              style={{}}
              searchable={true}
              searchBy={'name'}
              create={true}
              onCreateNew={onCreateNew}
            />
          )}
        </div>
      </Form.Group>
      {accessUntilVisible && (
        <div className={`form-group mb-2 `}>
          <label>{getLocalization('accessUntil')}</label>
          <Form.Control
            className={`form-control form-control-sm`}
            type="date"
            name="accessUntil"
            min={formatDate(new Date())}
            value={accessUntil}
            onChange={(e) => setAccessUntil(e.target.value)}
          />
        </div>
      )}
      <Form.Group className="mb-2">
        <Form.Label>{getLocalization('message')} :</Form.Label>
        <Form.Control
          name={'emailBody'}
          as={'textarea'}
          className={'form-control-sm'}
          placeholder={'Message'}
          value={emailBody}
          rows={3}
          // @ts-ignore
          onChange={(e) => setEmailBody(e.target.value)}
        />
      </Form.Group>
      {props.auto && (
        <Form.Group className="mb-2">
          <Form.Check
            id="share-ref-table-do-not-show"
            label={getLocalization('doNotShowAuto')}
            checked={Boolean(clientPersist.doNotShowShareAuto)}
            onChange={(e) => {
              dispatch(saveSetting('doNotShowShareAuto', `${e.target.checked}`));
            }}
          />
        </Form.Group>
      )}
    </div>
  );

  const transmit = () => {
    if (unregisteredUsers !== '' && !validEmail(unregisteredUsers)) {
      // setHasError(true);
      return;
    }

    if (value.length > 0 || unregisteredUsers.length > 0) {
      const nonGroup: string[] = [];
      const u = value.filter(v => v.groupId);
      u.forEach(v => {
        if (`${v.groupId}` !== `${clientPersist.groupId}`) {
          nonGroup.push(v.name);
        }
      });

      if (nonGroup.length > 0) {
        setGroupError(nonGroup);
        console.log(groupError);
        return;
      }
      abortController = new AbortController();
      const toastId = Date.now() + Math.floor(Math.random() * 100);
      toast(getLocalization('sending'), {
        toastId,
        type: toast.TYPE.INFO,
        autoClose: false
      });
      dispatch(sendActionEmail({
        userIds: value.map((u: User) => `${u.id}`),
        url: props.link,
        unregisteredUsers: newUsers.join(','),
        questionId: props.questionId,
        tableFormId: props.tableFormId,
        accessUntil,
        emailBody
      }, abortController.signal))
        .then(response => response.json()).then(json => {
          if (json.status === 'OK') {
            closeModal();
            // setShowModal(false);
            setValue([]);
            toast.update(toastId, {
              type: toast.TYPE.SUCCESS,
              render: getLocalization('sent')
            });
          } else {
            toast.update(toastId, {
              type: toast.TYPE.ERROR,
              render: getLocalization('emailSendingError')
            });
          }
          setTimeout(() => toast.dismiss(toastId), 3000);
        }).catch(err => {
          console.error(err);
          toast.update(toastId, {
            type: toast.TYPE.ERROR,
            render: getLocalization('emailSendingError')
          });
          setTimeout(() => toast.dismiss(toastId), 3000);
        });
      setGroupError(nonGroup);
    }
  };

  const onEntered = () => {
    setWidth('200px');
  };

  return (
    <ConfirmationModal
      onConfirm={transmit}
      onClose={() => closeModal()}
      localizations={{
        cancel: getLocalization('cancel'),
        confirm: getLocalization('send'),
        confirmStyle: 'success',
        header: (
          <label>{getLocalization('share')}</label>
        ),
        body: (modalBody)
      }}
      onEntered={onEntered}
    />
  );
}
